import {
  GraphQLNonNull,
  GraphQLObjectType,
  GraphQLInt,
  GraphQLString
} from 'graphql'
import { ContrastGraphQLFieldConfig } from '../'
import { ContrastQueryParameters } from '../endpoint-helpers'
import { AttestationReportRequest } from '../mutations/generate-attestation-report'
import { Context } from '../shared'
import {
  ReportingInfoQueryArgs,
  reportingInfoQueryArgs
} from '../types/reporting-info-query-args'

export function mapReportingInfoFilters(filters: AttestationReportRequest) {
  const {
    vulnerabilityStatuses,
    vulnerabilitySeverities,
    vulnerabilityTypes,
    vulnerabilityTags,
    serverEnvironments,
    serverTags,
    complianceReports,
    showVulnerabilitiesDetails,
    showRouteObservations
  } = filters

  return {
    vulnerabilityStatuses,
    vulnerabilitySeverities,
    vulnerabilityTypes,
    vulnerabilityTags,
    serverEnvironments,
    serverTags,
    complianceReports,
    showVulnerabilitiesDetails,
    showRouteObservations
  }
}

export function appendReportingInfoFiltersToParams(
  params: ContrastQueryParameters,
  filters: AttestationReportRequest
) {
  const mappedFilters = mapReportingInfoFilters(filters)

  const {
    vulnerabilityStatuses,
    vulnerabilitySeverities,
    vulnerabilityTypes,
    vulnerabilityTags,
    serverEnvironments,
    serverTags,
    complianceReports,
    showVulnerabilitiesDetails,
    showRouteObservations
  } = mappedFilters
  if (vulnerabilityStatuses) {
    vulnerabilityStatuses.forEach((vulnerabilityStatus) =>
      params.append('vulnerabilityStatuses', vulnerabilityStatus)
    )
  }
  if (vulnerabilitySeverities) {
    vulnerabilitySeverities.forEach((vulnerabilitySeverity) =>
      params.append('vulnerabilitySeverities', vulnerabilitySeverity)
    )
  }
  if (vulnerabilityTypes) {
    vulnerabilityTypes.forEach((vulnerabilityType) =>
      params.append('vulnerabilityTypes', vulnerabilityType)
    )
  }
  if (vulnerabilityTags) {
    vulnerabilityTags.forEach((vulnerabilityTag) =>
      params.append('vulnerabilityTags', vulnerabilityTag)
    )
  }
  if (serverEnvironments) {
    serverEnvironments.forEach((serverEnvironment) =>
      params.append('serverEnvironments', serverEnvironment)
    )
  }
  if (serverTags) {
    serverTags.forEach((serverTag) => params.append('serverTags', serverTag))
  }
  if (complianceReports) {
    complianceReports.forEach((complianceReport) =>
      params.append('complianceReports', complianceReport)
    )
  }
  if (showVulnerabilitiesDetails) {
    params.append('showVulnerabilitiesDetails', showVulnerabilitiesDetails)
  }
  if (showRouteObservations) {
    params.append('showRouteObservations', showRouteObservations)
  }
}

const reportingInfoType = new GraphQLObjectType({
  name: 'ReportingInfo',
  fields: () => ({
    vulnTotal: {
      description: 'Number of vulnerabilities for the given application',
      type: new GraphQLNonNull(GraphQLInt)
    },
    vulnerabilitiesWithDetailsLimit: {
      description:
        'The maximum number of vulnerability with details to generate an attestation report',
      type: new GraphQLNonNull(GraphQLInt)
    },
    vulnerabilitiesWithoutDetailsLimit: {
      description:
        'Limit of vulnerabilities without details to generate an attestation report',
      type: new GraphQLNonNull(GraphQLInt)
    },
    routesWithDetailsLimit: {
      description:
        'Limit of routes with details to generate an attestation report',
      type: new GraphQLNonNull(GraphQLInt)
    },
    routesWithoutDetailsLimit: {
      description:
        'Limit of routes without details to generate an attestation report',
      type: new GraphQLNonNull(GraphQLInt)
    },
    maxVulnsAndRoutesWithDetails: {
      description:
        'Limit of vulnerabilities and routes with details to generate an attestation report',
      type: new GraphQLNonNull(GraphQLInt)
    },
    maxVulnsAndRoutesWithoutDetails: {
      description:
        'Limit of vulnerabilities and routes without details to generate an attestation report',
      type: new GraphQLNonNull(GraphQLInt)
    },
    totalRoutes: {
      description: 'Number of routes for the given application',
      type: new GraphQLNonNull(GraphQLInt)
    },
    applicationName: {
      description: 'Application name',
      type: new GraphQLNonNull(GraphQLString)
    }
  })
})

export const reportingInfo: ContrastGraphQLFieldConfig<
  any,
  Context,
  ReportingInfoQueryArgs,
  {
    readonly vulnTotal: number
    readonly vulnerabilitiesWithDetailsLimit: number
    readonly vulnerabilitiesWithoutDetailsLimit: number
    readonly routesWithDetailsLimit: number
    readonly routesWithoutDetailsLimit: number
    readonly maxVulnsAndRoutesWithDetails: number
    readonly maxVulnsAndRoutesWithoutDetails: number
    readonly totalRoutes: number
    readonly applicationName: string
  }
> = {
  type: new GraphQLNonNull(reportingInfoType),
  description: 'Reporting extra information',
  args: reportingInfoQueryArgs,
  async resolve(
    _source,
    _args,
    context
  ): Promise<{
    readonly vulnTotal: number
    readonly vulnerabilitiesWithDetailsLimit: number
    readonly vulnerabilitiesWithoutDetailsLimit: number
    readonly routesWithDetailsLimit: number
    readonly routesWithoutDetailsLimit: number
    readonly maxVulnsAndRoutesWithDetails: number
    readonly maxVulnsAndRoutesWithoutDetails: number
    readonly totalRoutes: number
    readonly applicationName: string
  }> {
    const { organizationID, applicationID, filters } = _args

    const params = new ContrastQueryParameters()
    appendReportingInfoFiltersToParams(params, filters)

    const {
      vulnerability_total,
      total_routes,
      vulnerabilities_with_details_limit,
      vulnerabilities_without_details_limit,
      routes_with_details_limit,
      routes_without_details_limit,
      max_vulns_and_routes_with_details,
      max_vulns_and_routes_without_details,
      application_name
    } = await context.connector.get(
      `/${organizationID}/applications/${applicationID}/attestation?${params}`
    )

    return {
      vulnTotal: vulnerability_total,
      vulnerabilitiesWithDetailsLimit: vulnerabilities_with_details_limit,
      vulnerabilitiesWithoutDetailsLimit: vulnerabilities_without_details_limit,
      routesWithDetailsLimit: routes_with_details_limit,
      routesWithoutDetailsLimit: routes_without_details_limit,
      maxVulnsAndRoutesWithDetails: max_vulns_and_routes_with_details,
      maxVulnsAndRoutesWithoutDetails: max_vulns_and_routes_without_details,
      totalRoutes: total_routes,
      applicationName: application_name
    }
  }
}
